// Adiantamento do cliente POR PARCELA do fornecedor no Efetivo (D-0095, Rafael 24/10).
//
// O operador (Eventos) põe o adiantamento na(s) parcela(s) que quiser — tudo numa ou dividido. O total
// do fornecedor é a soma das parcelas. O sistema sugere: preenche a partir da 1ª parcela em aberto e o
// que sobra vai para a próxima; o operador ajusta.
//
// Parcela coberta pelo adiantamento — mesmo inteira — NÃO fica paga: quem dá a baixa é o Financeiro.
//
// Legado (atribuído no fornecedor antes da divisão): 1 parcela → é dela; 2+ → "sem parcela", nenhuma
// parcela é descontada até o operador distribuir. Camada 2: AdiantamentoParcela::validar (API), com as
// MESMAS mensagens.

export const MSG_REMOVER_PELA_ABA = 'Para remover o adiantamento deste fornecedor, use a aba Adiantamentos '
    + '(botão Remover) — lá ficam o motivo e as travas de segurança.';
export const MSG_LEGADO_PAGO = 'O adiantamento deste fornecedor foi atribuído antes da divisão por parcela e já há parcela paga: '
    + 'ele não pode ser redistribuído. Para usar mais adiantamento, use o saldo do evento em outro fornecedor.';
const MSG_TRAVADA = 'O Financeiro já travou este pagamento (está pagando). Para mudar, ligue para o Financeiro.';

const centavos = (v) => Math.round((parseFloat(v) || 0) * 100);
const moeda = (v) => (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Parcelas de previsão do fornecedor (mesma ordem/numeração da tela) com o que as regras precisam.
export function parcelasDoAdiantamento(forn) {
    const lista = (forn && (forn.parcelas_aprovadas || forn.parcelas)) || [];
    return lista.map((p, i) => ({
        id: String(p.id),
        numero: String(i + 1).padStart(2, '0'),
        valor: parseFloat(p.valor) || 0,
        atual: parseFloat(p.valor_adiantamento_parcela) || 0,
        paga: !!p.pago,
        travada: !!p.travado_pagamento,
        liberada: !!p.liberado_financeiro_at,
        aprovada: !!p.liberado_financeiro_at || forn.status === 'aprovado',
    }));
}

// Parcela que a sugestão não mexe (mantém a parte que tem) e que não recebe adiantamento novo.
export function parcelaBloqueada(p) {
    return !!(p.paga || p.travada || p.liberada || !p.aprovada);
}

// Campo da parte editável? Parcela não aprovada que já tem parte fica aberta só para REDUZIR (a validação
// barra aumentar), igual à API.
export function campoAdiantamentoBloqueado(p) {
    return !!(p.paga || p.travada || p.liberada || (!p.aprovada && centavos(p.atual) <= 0));
}

export function motivoBloqueio(p) {
    if (p.paga) return 'Paga';
    if (p.travada) return 'Travada pelo Financeiro';
    if (p.liberada) return 'Em Contas Pendentes';
    if (!p.aprovada) return 'Aguardando aprovação';
    return null;
}

// Sugestão: 1ª parcela em aberto primeiro, o resto vai para a próxima. Bloqueada mantém o que tem.
export function sugerirDistribuicao(total, parcelas) {
    const sugestao = {};
    let resto = centavos(total);
    parcelas.forEach((p) => {
        if (parcelaBloqueada(p)) {
            const atual = centavos(p.atual);
            sugestao[p.id] = atual / 100;
            resto -= atual;
        }
    });
    parcelas.forEach((p) => {
        if (parcelaBloqueada(p)) return;
        const parte = Math.max(0, Math.min(centavos(p.valor), resto));
        sugestao[p.id] = parte / 100;
        resto -= parte;
    });
    return sugestao;
}

export function totalDistribuicao(parcelas, pedido) {
    const soma = parcelas.reduce((s, p) => s + (Object.prototype.hasOwnProperty.call(pedido || {}, p.id)
        ? centavos(pedido[p.id]) : centavos(p.atual)), 0);
    return soma / 100;
}

export function distribuicaoMudou(parcelas, pedido) {
    return parcelas.some((p) => Object.prototype.hasOwnProperty.call(pedido || {}, p.id)
        && centavos(pedido[p.id]) !== centavos(p.atual));
}

// Lista de avisos (vazia = pode salvar). `disponivel` = saldo do evento na tela (meta), que já desconta o
// que este fornecedor usa hoje; por isso o limite é disponivel + totalAtual (o servidor ignora o débito dele).
export function validarDistribuicao({ parcelas, pedido, disponivel, totalAtual, legado }) {
    const erros = [];
    const total = centavos(totalAtual);
    pedido = pedido || {};

    if (legado && parcelas.some((p) => p.paga) && distribuicaoMudou(parcelas, pedido)) {
        return [MSG_LEGADO_PAGO];
    }

    let soma = 0;
    parcelas.forEach((p) => {
        const veio = Object.prototype.hasOwnProperty.call(pedido, p.id);
        const parte = veio ? centavos(pedido[p.id]) : centavos(p.atual);
        soma += parte;
        if (!veio) return;
        const rotulo = 'Parcela ' + p.numero;
        if (parte < 0) { erros.push(rotulo + ': o adiantamento não pode ser negativo.'); return; }
        // Parcela que não mudou não é julgada de novo (mesma ordem da API).
        if (parte === centavos(p.atual)) return;
        if (parte > centavos(p.valor)) {
            erros.push(rotulo + ' é de R$ ' + moeda(p.valor) + ': o adiantamento nela não pode passar desse valor.');
            return;
        }
        if (p.paga) erros.push(rotulo + ' já foi paga: o adiantamento dela não muda.');
        else if (p.travada) erros.push(rotulo + ': ' + MSG_TRAVADA);
        else if (p.liberada) erros.push(rotulo + ' já está em Contas Pendentes: para mudar o adiantamento dela, peça ao Financeiro para usar "Voltar para Custo Previsto".');
        else if (!p.aprovada && parte > centavos(p.atual)) erros.push(rotulo + ' ainda não foi aprovada pelo Financeiro: o adiantamento só vai para parcela aprovada.');
    });

    if (soma <= 0 && total > 0) erros.push(MSG_REMOVER_PELA_ABA);
    const limite = centavos(disponivel) + total;
    if (soma > total && soma > limite) {
        erros.push('Saldo de adiantamento insuficiente neste evento: disponível R$ ' + moeda(limite / 100)
            + '. Se o valor na tela estava diferente, recarregue — outra pessoa pode ter usado o saldo.');
    }
    return erros;
}

export function aPagar(valorParcela, parte) {
    return Math.max(0, centavos(valorParcela) - centavos(parte)) / 100;
}

// Cobertura NÃO é baixa: 'paga' só com a baixa do Financeiro.
export function situacaoParcela(valorParcela, parte, paga) {
    if (paga) return 'paga';
    const c = centavos(parte);
    if (c <= 0) return 'aberta';
    return c >= centavos(valorParcela) ? 'coberta' : 'parcial';
}

// Financeiro (Contas Pendentes/Pagas/Detalhe): quanto ESTA conta desconta do adiantamento, a partir do
// `adiantamento_fornecedor` que a API anexa. null = não desconta nada nesta conta (ou o fornecedor
// está "sem parcela"). Compatível com a API antiga (sem `valor_parcela`): 1 parcela = o total.
export function parteAdiantamentoDaConta(ad) {
    if (!ad || !(parseFloat(ad.valor) > 0)) return null;
    if (ad.sem_parcela) return null;
    if (ad.valor_parcela !== undefined) {
        const parte = parseFloat(ad.valor_parcela) || 0;
        return parte > 0 ? parte : null;
    }
    return (ad.parcelas_fornecedor || 1) === 1 ? parseFloat(ad.valor) : null;
}

// Estado do adiantamento do Efetivo montado a partir do que a API devolveu para o fornecedor.
// `adiant_origem` guarda a "assinatura" do que veio do servidor: quando a tela recarrega os fornecedores
// e a assinatura mudou (salvou, removeu na aba Adiantamentos, ganhou parcela), o estado é refeito; sem
// isso o próximo Salvar reenviava a distribuição antiga e regravava um adiantamento já removido.
export function assinaturaAdiantamento(forn) {
    const ps = parcelasDoAdiantamento(forn || {});
    return [
        (forn && forn.usar_adiantamento) || 'nao',
        centavos(forn && forn.valor_adiantamento),
        // Inclui os estados: parcela liberada/paga/travada pelo Financeiro enquanto o operador digitava também
        // refaz o estado (senão a digitação velha travava o Salvar até um F5).
        ps.map(p => [p.id, centavos(p.atual), centavos(p.valor), p.paga ? 1 : 0, p.travada ? 1 : 0, p.liberada ? 1 : 0, p.aprovada ? 1 : 0].join(':')).join(','),
    ].join('|');
}

export function camposAdiantamentoDoFornecedor(forn) {
    return {
        usar_adiantamento:    forn.usar_adiantamento || 'nao',
        valor_adiantamento:   forn.valor_adiantamento ?? 0,
        percent_adiantamento: forn.percent_adiantamento ?? 0,
        adiant_parcelas:      parcelasDoAdiantamento(forn).reduce((acc, p) => { acc[p.id] = p.atual; return acc; }, {}),
        adiant_origem:        assinaturaAdiantamento(forn),
    };
}

// A conta mostra a linha do adiantamento? Sim quando desconta nesta parcela, quando o fornecedor está
// "sem parcela" e na API antiga (sem `valor_parcela`). Conta de fornecedor dividido sem parte nesta parcela
// não mostra nada.
export function mostrarAdiantamentoNaConta(ad) {
    if (!ad || !(parseFloat(ad.valor) > 0)) return false;
    return parteAdiantamentoDaConta(ad) !== null || !!ad.sem_parcela || ad.valor_parcela === undefined;
}
